// Communications panel — shared between Creator Dashboard ("Communications" tab)
// and Super Admin. Both surfaces get:
//   · Compose broadcast (silo picker → load subscribers → subject → message → send)
//   · Export subscriber emails as .txt (one per line)
//   · Collapsible platform-email catalog with preview + toggles
// Super Admin adds: "Entire platform" scope in silo picker + access to toggle
// platform-wide flags that creators can only override for themselves.
import { useEffect, useMemo, useState } from "react";
import { Megaphone, PaperPlaneTilt, DownloadSimple, CaretRight, CaretDown, Eye, LockSimple, FloppyDisk, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
// iter-35b: TipsModule moved to EarningsDashboard (was previously imported here)

export default function CommunicationsPanel({ isAdmin = false, section = "all" }) {
  const [silos, setSilos] = useState([]);
  const [selectedSilo, setSelectedSilo] = useState("");  // '' = pick one, '__all__' = platform (admin only)
  // iter-35a: cross-silo audience filter from Users tab. When set, the
  // selectedSilo dropdown is bypassed — the panel sends to that audience
  // across all of the creator's silos. Cleared via the dismiss button.
  const [audienceFilter, setAudienceFilter] = useState(null); // 'paying'|'non_paying'|'creators'|null
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  // To-field (May 11 update) — semicolon/comma/newline-separated list of
  // recipient emails. Always the authoritative source of who gets the
  // email. "Load subscribers" auto-fills this; users can also paste their
  // own list. Send is enabled when this contains >=1 valid address.
  const [toRaw, setToRaw] = useState("");
  const [showToTip, setShowToTip] = useState(false);
  const [subscribers, setSubscribers] = useState(null);  // {count, emails}
  const [sending, setSending] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);

  // Load creator's silos (or all silos for admin) on mount
  useEffect(() => {
    (async () => {
      try {
        const url = isAdmin ? "/admin/silos" : "/silos";
        const r = await api.get(url);
        setSilos(r.data || []);
      } catch { /* silent */ }
    })();
  }, [isAdmin]);

  // iter-35a: read prefilter set by UsersTab "Email this group" buttons.
  // Two paths: (a) sessionStorage on initial mount (cross-route arrival),
  // (b) custom event when user is already on dashboard and clicks the button
  // (tab switch alone doesn't re-mount this panel).
  useEffect(() => {
    const applyFilter = (filter) => {
      if (!filter || !["paying", "non_paying", "creators", "interactions"].includes(filter)) return;
      setAudienceFilter(filter);
      setSubscribers(null);
      (async () => {
        setLoadingSubs(true);
        try {
          const r = await api.get("/communications/subscribers", {
            params: { scope: "me", audience: filter },
          });
          setSubscribers(r.data);
          if (r.data.count === 0) toast("No active members in that group yet.", { icon: "ℹ" });
        } catch (e) {
          toast.error(e.response?.data?.detail || "Could not load audience.");
        } finally { setLoadingSubs(false); }
      })();
    };

    // Path (a): cross-route arrival
    const stored = sessionStorage.getItem("comms_audience_filter");
    if (stored) {
      sessionStorage.removeItem("comms_audience_filter");
      applyFilter(stored);
    }

    // Path (b): same-route dispatch from UsersTab
    const onSetAudience = (e) => {
      const filter = e?.detail?.audience;
      // Also clear sessionStorage so remount path doesn't double-fire
      sessionStorage.removeItem("comms_audience_filter");
      applyFilter(filter);
    };
    window.addEventListener("comms-set-audience", onSetAudience);
    return () => window.removeEventListener("comms-set-audience", onSetAudience);
  }, []);

  // Marketing & Com tab — when the user clicks "Insert marketing email"
  // up in the silo list, MarketingPanel fires this event with the
  // pre-built subject + body (silo URL already substituted in). We pre-fill
  // ONLY the compose subject + message. We do NOT auto-load subscribers.
  //
  // Feb 17, 2026 — subscriber pre-load removed per user spec. Recipients
  // should only ever populate from explicit user action ("Email this group"
  // buttons or manual paste). The Insert button is for content only.
  useEffect(() => {
    const onInsertBroadcast = (e) => {
      const { subject: s, message: m } = e?.detail || {};
      if (typeof s === "string") setSubject(s.slice(0, 120));
      if (typeof m === "string") setMessage(m.slice(0, 10_000));
      // Scroll the compose card into view so the user sees what landed.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="communications-compose"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    };
    window.addEventListener("marketing-insert-broadcast", onInsertBroadcast);
    return () => window.removeEventListener("marketing-insert-broadcast", onInsertBroadcast);
  }, []);

  const loadSubscribers = async () => {
    if (audienceFilter) {
      // Reload using the audience filter
      setLoadingSubs(true);
      setSubscribers(null);
      try {
        const r = await api.get("/communications/subscribers", {
          params: { scope: "me", audience: audienceFilter },
        });
        setSubscribers(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load.");
      } finally { setLoadingSubs(false); }
      return;
    }
    if (!selectedSilo) return;
    setLoadingSubs(true);
    setSubscribers(null);
    try {
      const params = selectedSilo === "__all__"
        ? { scope: "all" }
        : { silo_id: selectedSilo, scope: "me" };
      const r = await api.get("/communications/subscribers", { params });
      setSubscribers(r.data);
      if (r.data.count === 0) toast("No active subscribers found for that target.", { icon: "ℹ" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load subscribers.");
    } finally { setLoadingSubs(false); }
  };

  // Auto-populate the "To" field whenever subscribers get (re)loaded so
  // the creator doesn't have to manually merge lists. If the user has
  // already typed/pasted something into "To", we DON'T clobber it — only
  // populate when the field is empty. Lets manual pastes take priority
  // over subscriber loads.
  useEffect(() => {
    if (!subscribers?.emails?.length) return;
    if (toRaw.trim()) return; // user already has something in the field
    setToRaw(subscribers.emails.join(", "));
  }, [subscribers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse the "To" field into a clean, deduped list of valid emails.
  const parsedRecipients = useMemo(() => {
    if (!toRaw) return [];
    const tokens = toRaw
      .split(/[\s,;]+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const rx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const seen = new Set();
    const out = [];
    for (const t of tokens) {
      if (rx.test(t) && !seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
    return out;
  }, [toRaw]);
  const invalidTokenCount = useMemo(() => {
    if (!toRaw) return 0;
    const tokens = toRaw.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
    return tokens.length - parsedRecipients.length;
  }, [toRaw, parsedRecipients]);

  const exportCsv = () => {
    if (!subscribers?.emails?.length) {
      toast.error("Load subscribers first.");
      return;
    }
    const blob = new Blob([subscribers.emails.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const silo = silos.find((s) => s.id === selectedSilo);
    a.download = selectedSilo === "__all__"
      ? `mysilo-all-subscribers-${Date.now()}.txt`
      : `mysilo-${silo?.slug || "silo"}-subscribers-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${subscribers.count} email${subscribers.count === 1 ? "" : "s"}.`);
  };

  const sendBroadcast = async () => {
    if (parsedRecipients.length === 0) return toast.error("Add at least one valid email to the \"To\" field.");
    if (subject.trim().length < 3) return toast.error("Subject is too short.");
    if (message.trim().length < 10) return toast.error("Message is too short.");
    if (!window.confirm(`Send this email to ${parsedRecipients.length} recipient${parsedRecipients.length === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      // recipient_emails is the authoritative recipient list — it ALWAYS
      // takes priority over audience/scope resolution server-side. We still
      // pass silo_id when picked so the email's HTML footer shows the
      // silo name (and the per-silo daily cap applies if applicable).
      const body = {
        subject,
        message,
        recipient_emails: parsedRecipients,
        ...(selectedSilo && selectedSilo !== "__all__"
          ? { scope: "silo", silo_id: selectedSilo }
          : selectedSilo === "__all__"
            ? { scope: "all" }
            : { scope: "me" }),
      };
      const r = await api.post("/communications/broadcast", body);
      toast.success(`Broadcast dispatched to ${r.data.sent_count} recipients.`);
      setSubject("");
      setMessage("");
      setToRaw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed.");
    } finally { setSending(false); }
  };

  const sendTest = async () => {
    if (subject.trim().length < 3) return toast.error("Subject is too short.");
    if (message.trim().length < 10) return toast.error("Message is too short.");
    setSending(true);
    try {
      const body = {
        subject,
        message,
        test_only: true,
        // Pass silo_id if known so the test renders the same footer as the real send.
        ...(selectedSilo && selectedSilo !== "__all__" ? { scope: "silo", silo_id: selectedSilo } : { scope: "me" }),
      };
      const r = await api.post("/communications/broadcast", body);
      toast.success(`Test email sent to ${r.data.to}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed.");
    } finally { setSending(false); }
  };

  const adminOpt = isAdmin ? [{ id: "__all__", name: "— Entire platform (all silos) —", slug: "platform-wide" }] : [];
  const siloOptions = [...adminOpt, ...silos];

  return (
    <div className="space-y-6" data-testid="communications-panel">
      {/* Compose Broadcast ==================================================== */}
      {(section === "all" || section === "compose") && (
      <div className="bg-white border hairline rounded-lg p-6" data-testid="communications-compose">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
            <Megaphone size={20} weight="duotone" />
          </div>
          <div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A]">Compose broadcast</h3>
            <p className="text-xs text-[#6B6A68] mt-1 max-w-xl">
              Send a custom email to your subscribers — updates, new silo launches, a simple thank-you, whatever feels right.
              {isAdmin && <span className="block mt-0.5 text-[#C16E5A]">Super Admin: you can also target the entire platform at once.</span>}
            </p>
          </div>
        </div>

        {/* iter-35a: audience prefilter banner — set by UsersTab "Email this group" buttons. */}
        {audienceFilter && (
          <div className="mb-4 p-3 bg-[#FDF2EE] border border-[#C16E5A]/30 rounded-md flex items-center gap-3" data-testid="comms-audience-banner">
            <span className="text-xs uppercase tracking-[0.22em] text-[#C16E5A] font-medium">Filter active</span>
            <span className="text-sm text-[#1C1B1A]">
              Sending to <strong>{
                audienceFilter === "non_paying" ? "Fans (non-paying)" :
                audienceFilter === "paying" ? "Paying Subscribers" :
                audienceFilter === "interactions" ? "Interactions (warm leads)" :
                "Creators (your referral base)"
              }</strong> across all your silos.
            </span>
            <button
              onClick={() => { setAudienceFilter(null); setSubscribers(null); }}
              data-testid="comms-audience-clear"
              className="ml-auto text-xs text-[#6B6A68] hover:text-[#C16E5A] underline"
            >
              Clear filter
            </button>
          </div>
        )}

        {!audienceFilter && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Target silo</label>
              <select value={selectedSilo} onChange={(e) => { setSelectedSilo(e.target.value); setSubscribers(null); }}
                data-testid="comms-silo-picker"
                className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
                <option value="">Pick a silo…</option>
                {siloOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button onClick={loadSubscribers} disabled={!selectedSilo || loadingSubs} data-testid="comms-load-subs"
                className="flex-1 text-sm px-3 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40">
                {loadingSubs ? "Loading…" : "Load subscribers"}
              </button>
            </div>
          </div>
        )}

        {subscribers && (
          <div className="mb-4 p-3 bg-[#F0F7F2] rounded-md border border-[#C8DCC8] flex items-center justify-between" data-testid="comms-subscribers-loaded">
            <div className="text-sm text-[#2F6B3A]">
              <strong className="tabular-nums">{subscribers.count}</strong> active subscriber{subscribers.count === 1 ? "" : "s"} loaded.
            </div>
            <button onClick={exportCsv} disabled={!subscribers.count} data-testid="comms-export-csv"
              className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] flex items-center gap-1.5">
              <DownloadSimple size={12} weight="duotone" /> Export emails (.txt)
            </button>
          </div>
        )}

        <label className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">
          <span className="flex items-center gap-2">
            To
            <span className="relative inline-flex">
              <button
                type="button"
                onClick={() => setShowToTip((v) => !v)}
                data-testid="comms-to-tip-btn"
                title="Bulk email guide — click to expand"
                className={`inline-flex items-center transition-colors ${showToTip ? "text-[#C16E5A]" : "text-[#6B6A68] hover:text-[#4A5D4E]"}`}
                aria-label="Bulk email guide"
              >
                <Eye size={14} weight="duotone" />
              </button>
              {showToTip && (
                <>
                  {/* Click-outside catcher */}
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setShowToTip(false)}
                    aria-hidden="true"
                  />
                  <div
                    data-testid="comms-to-tip-content"
                    className="absolute left-6 top-0 z-30 w-[440px] max-w-[85vw] bg-white border border-[#D8D6D2] rounded-lg shadow-xl p-4 text-[#1C1B1A] normal-case tracking-normal text-left"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="text-sm font-semibold text-[#1C1B1A]">Bulk email — the short, honest guide</div>
                      <button
                        type="button"
                        onClick={() => setShowToTip(false)}
                        className="text-[#6B6A68] hover:text-[#1C1B1A] text-lg leading-none -mt-1"
                        aria-label="Close"
                      >×</button>
                    </div>
                    <ul className="text-xs text-[#1C1B1A] leading-relaxed list-disc pl-4 space-y-1.5">
                      <li>
                        <strong>How "To" works.</strong> Whatever is in this field is exactly who gets the email. Paste your own list, or hit "Load subscribers" to auto-fill from this silo. Edit freely before sending. Capped at 1,000 recipients per send.
                      </li>
                      <li>
                        <strong>Formats accepted.</strong> Commas, semicolons, spaces, or new lines — all work. Bad entries are silently dropped before sending.
                      </li>
                      <li>
                        <strong>Resend (our email provider) limits:</strong>
                        <ul className="list-disc pl-4 mt-0.5 space-y-0.5">
                          <li><em>Free tier:</em> 100 emails/day, 3,000/month, no custom domain.</li>
                          <li><em>Pro ($20/mo):</em> 50,000 emails/month + custom domain support.</li>
                          <li><em>Scale ($90/mo+):</em> 100k+/month, higher rate limits, dedicated IP add-on.</li>
                        </ul>
                      </li>
                      <li>
                        <strong>CAN-SPAM &amp; the law (US):</strong> every commercial email must have (a) a non-deceptive subject line, (b) a real physical mailing address in the footer, (c) a working unsubscribe path honored within 10 business days, (d) clear identification as a commercial message if it is one. Knowing violations: $50,120 per email.
                      </li>
                      <li>
                        <strong>GDPR (EU recipients):</strong> you need explicit consent OR a legitimate-interest basis for emailing them. "I scraped their address" is not a basis. Keep proof of opt-in.
                      </li>
                      <li>
                        <strong>CASL (Canada):</strong> express consent required, identification + unsubscribe mandatory. Fines up to CA$10M.
                      </li>
                      <li>
                        <strong>Practical rule of thumb.</strong> Only email people who asked to hear from you, or whose business email is publicly listed AND your message is relevant to their job. Cold blasts to scraped lists = deliverability suicide and legal exposure.
                      </li>
                    </ul>
                  </div>
                </>
              )}
            </span>
          </span>
          {parsedRecipients.length > 0 && (
            <span className="text-[10px] text-[#6B6A68] normal-case tracking-normal" data-testid="comms-to-count">
              {parsedRecipients.length.toLocaleString()} recipient{parsedRecipients.length === 1 ? "" : "s"}
              {invalidTokenCount > 0 && <span className="text-[#C16E5A] ml-1">· {invalidTokenCount} invalid</span>}
            </span>
          )}
        </label>
        <textarea
          value={toRaw}
          onChange={(e) => setToRaw(e.target.value)}
          rows={3}
          placeholder="Hit &quot;Load subscribers&quot; above to auto-fill, or paste a list — commas, semicolons, spaces, or newlines all work."
          data-testid="comms-to"
          className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] mb-1 font-mono"
        />
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setToRaw("")}
            disabled={!toRaw}
            data-testid="comms-to-clear"
            className="text-[10px] text-[#6B6A68] hover:text-[#C16E5A] disabled:opacity-30"
          >
            Clear list
          </button>
          <span className="text-[10px] text-[#6B6A68]">{toRaw.length.toLocaleString()} chars</span>
        </div>

        <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Subject</label>
        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 120))}
          maxLength={120} placeholder="e.g., New silo just launched!"
          data-testid="comms-subject"
          className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] mb-1" />
        <div className="text-[10px] text-[#6B6A68] mb-3 text-right">{subject.length}/120</div>

        <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Message</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value.slice(0, 10_000))}
          rows={8} maxLength={10_000}
          placeholder="Write something nice for your subscribers. Line breaks will be preserved in the email."
          data-testid="comms-message"
          className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] mb-1 font-mono" />
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] text-[#6B6A68]">We wrap this in a branded HTML shell with your name + a platform-attribution footer.</span>
          <span className="text-[10px] text-[#6B6A68]">{message.length.toLocaleString()}/10,000</span>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={sendTest}
            disabled={sending || subject.trim().length < 3 || message.trim().length < 10}
            data-testid="comms-test"
            title="Send a test copy to your own email — preview before blasting"
            className="text-xs px-4 py-2.5 rounded-md border-2 border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#4A5D4E] hover:text-white disabled:opacity-40 flex items-center gap-1.5 font-medium uppercase tracking-[0.18em]"
          >
            <PaperPlaneTilt size={12} weight="duotone" /> Test
          </button>
          <button onClick={sendBroadcast} disabled={sending || parsedRecipients.length === 0 || !subject || !message}
            data-testid="comms-send"
            className="text-sm px-5 py-2.5 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] disabled:opacity-40 flex items-center gap-1.5">
            <PaperPlaneTilt size={14} weight="duotone" />
            {sending ? "Sending…" : (parsedRecipients.length ? `Send to ${parsedRecipients.length}` : "Send")}
          </button>
        </div>
      </div>
      )}

      {/* Platform email catalog ============================================== */}
      {(section === "all" || section === "catalog") && (
        <EmailCatalogSection isAdmin={isAdmin} />
      )}

      {/* iter-35b: Tips were relocated to the new "Your Earnings" tab where
          they belong (alongside subs and referrals). Communications stays
          focused on outbound messaging. */}
    </div>
  );
}

function EmailCatalogSection({ isAdmin }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});   // creator-scoped
  const [savingKey, setSavingKey] = useState(null);
  // Jun 2026 (email sweep) — one-click proof that every catalogued email can
  // actually leave the building, without staging 40 real refunds first.
  const [selfTesting, setSelfTesting] = useState(false);
  const [selfTestResult, setSelfTestResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/communications/email-catalog");
      setData(r.data);
    } catch { /* silent */ }
    try {
      const r = await api.get("/me/threshold-overrides");
      setOverrides(r.data.overrides || {});
    } catch { /* silent */ }
  };

  useEffect(() => { if (open && !data) load(); }, [open, data]);

  const toggleForMe = async (toggleKey, currentVal) => {
    setSavingKey(toggleKey);
    try {
      await api.patch("/me/threshold-overrides", { updates: { [toggleKey]: !currentVal } });
      toast.success(`Saved preference.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save toggle.");
    } finally { setSavingKey(null); }
  };

  const toggleForPlatform = async (toggleKey, currentVal) => {
    setSavingKey(toggleKey);
    try {
      await api.patch("/admin/thresholds", { updates: { [toggleKey]: !currentVal } });
      toast.success(`Platform setting updated.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save platform toggle.");
    } finally { setSavingKey(null); }
  };

  const runSelfTest = async () => {
    setSelfTesting(true);
    setSelfTestResult(null);
    try {
      const r = await api.post("/admin/email-selftest", {});
      setSelfTestResult(r.data);
      toast.success(`Sent ${r.data.sent} of ${r.data.sent + r.data.failed} to ${r.data.to}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Self-test could not run.");
    } finally { setSelfTesting(false); }
  };

  return (
    <div className="bg-white border hairline rounded-lg" data-testid="email-catalog-section">
      <button onClick={() => setOpen((o) => !o)} data-testid="email-catalog-toggle"
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#F0F1EE] transition-colors rounded-t-lg">
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
          <span className="font-medium text-[#1C1B1A]">Platform email catalog</span>
          <span className="text-xs text-[#6B6A68]">
            — every email the platform can send, when, and which you can turn off
            {data?.counts ? ` (${data.counts.user + data.counts.admin + data.counts.manual})` : ""}
          </span>
        </div>
        <span className="text-[10px] text-[#6B6A68] uppercase tracking-[0.18em]">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="border-t hairline px-5 py-4 space-y-5" data-testid="email-catalog-body">
          {!data ? (
            <div className="text-sm text-[#6B6A68]">Loading catalog…</div>
          ) : (() => {
            // Sprint Feb 8, 2026 — partition catalog by category.
            // Jun 2026 (email sweep) — admin/ops emails are now their own
            // section. Before this sweep none of them appeared at all: 31 of
            // the platform's 46 sendable emails were invisible here.
            const automated = data.catalog.filter((i) => i.category === "user" || !i.category);
            const adminOps = data.catalog.filter((i) => i.category === "admin");
            const manual = data.catalog.filter((i) => i.category === "manual");
            const del = data.deliverability;
            const renderRow = (item) => {
              const isToggleable = !item.fixed && item.toggle_key;
              const platformVal = data.toggles?.[item.id] ?? true;
              const effectiveOn = platformVal;
              const creatorOverridable = item.creator_overridable;
              const isManual = item.category === "manual";
              return (
                <div key={item.id} className="p-3 border hairline rounded-md" data-testid={`email-catalog-row-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium text-[#1C1B1A]">{item.title}</div>
                        {isManual && (
                          <span className="text-[9px] px-2 py-0.5 rounded bg-[#E7E1F5] text-[#4B3A7A] uppercase tracking-[0.18em]">User-triggered</span>
                        )}
                        {item.fixed && !isManual && (
                          <span className="text-[9px] px-2 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] inline-flex items-center gap-0.5 uppercase tracking-[0.18em]">
                            <LockSimple size={9} weight="duotone" /> Fixed
                          </span>
                        )}
                        {item.compliance && (
                          <span className="text-[9px] px-2 py-0.5 rounded bg-[#E4EDE6] text-[#2F6B3A] uppercase tracking-[0.18em]"
                            title="A financial or legal record — can never be switched off">Required</span>
                        )}
                        {item.commercial && (
                          <span className="text-[9px] px-2 py-0.5 rounded bg-[#FBF0DC] text-[#8A6A22] uppercase tracking-[0.18em]"
                            title="Promotional mail — carries the postal address and an unsubscribe link">Commercial</span>
                        )}
                        {item.creator_controlled && (
                          <span className="text-[9px] px-2 py-0.5 rounded bg-[#FDF1ED] text-[#C16E5A] uppercase tracking-[0.18em]">Creator-controlled</span>
                        )}
                        {isToggleable && (
                          <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-[0.18em] ${effectiveOn ? "bg-[#D4EDDA] text-[#2F6B3A]" : "bg-[#F0F1EE] text-[#6B6A68]"}`}>
                            {effectiveOn ? "On" : "Off"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6B6A68] mt-1 leading-relaxed">
                        <span className="uppercase tracking-[0.18em] text-[10px] text-[#4A5D4E] mr-1">When:</span>
                        {item.when}
                      </div>
                      <div className="text-xs text-[#6B6A68] mt-0.5">
                        <span className="uppercase tracking-[0.18em] text-[10px] text-[#4A5D4E] mr-1">To:</span> {item.to}
                      </div>
                      <div className="text-xs text-[#6B6A68] mt-0.5 font-mono truncate">
                        <span className="uppercase tracking-[0.18em] text-[10px] text-[#4A5D4E] mr-1 font-sans">Subject:</span> {item.subject_template}
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5">
                      <button onClick={() => setPreview(item)} data-testid={`email-preview-${item.id}`}
                        className="text-xs px-3 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1 text-[#6B6A68] hover:text-[#1C1B1A]">
                        <Eye size={12} weight="duotone" /> Preview
                      </button>
                      {isToggleable && isAdmin && (
                        <button onClick={() => toggleForPlatform(item.toggle_key, effectiveOn)}
                          disabled={savingKey === item.toggle_key}
                          data-testid={`email-toggle-platform-${item.id}`}
                          title="Platform-wide toggle (super admin)"
                          className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1 disabled:opacity-50 ${effectiveOn ? "bg-[#4A5D4E] text-white" : "bg-[#F0F1EE] text-[#6B6A68]"}`}>
                          <Globe size={11} /> {effectiveOn ? "On" : "Off"}
                        </button>
                      )}
                      {isToggleable && !isAdmin && creatorOverridable && (
                        <button onClick={() => toggleForMe(item.toggle_key, effectiveOn)}
                          disabled={savingKey === item.toggle_key}
                          data-testid={`email-toggle-me-${item.id}`}
                          title="Your personal override"
                          className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1 disabled:opacity-50 ${effectiveOn ? "bg-[#4A5D4E] text-white" : "bg-[#F0F1EE] text-[#6B6A68]"}`}>
                          <FloppyDisk size={11} /> {effectiveOn ? "On" : "Off"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            };
            return (
              <>
                {del && (
                  <div className="space-y-2" data-testid="email-deliverability">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A5D4E] flex items-center gap-1.5">
                      <Globe size={10} weight="duotone" /> Deliverability
                    </div>
                    {!del.mailing_address_set && (
                      <div className="p-3 rounded-md bg-[#FDF1ED] border border-[#E8C4B8] text-xs text-[#8A3A22] leading-relaxed"
                        data-testid="email-warn-address">
                        <strong>No postal address configured.</strong> Broadcasts and Silo Wars recruit
                        invites are blocked until one is set — commercial email legally requires a physical
                        address in the footer. A street address, a USPS-registered PO Box, or a virtual
                        mailbox (PMB) all count. Set <code>PLATFORM_MAILING_ADDRESS</code> in the backend
                        environment. Never use your home address.
                      </div>
                    )}
                    {!del.bounce_webhook_configured && (
                      <div className="p-3 rounded-md bg-[#FDF1ED] border border-[#E8C4B8] text-xs text-[#8A3A22] leading-relaxed"
                        data-testid="email-warn-bounces">
                        <strong>Bounces and spam complaints are not being handled.</strong> Dead addresses
                        keep getting mailed, and mailbox providers charge that to your whole domain — the
                        password reset is the first thing to stop arriving. Add a webhook in Resend →
                        Webhooks for <code>email.bounced</code> and <code>email.complained</code> pointing
                        at <code>/api/webhooks/resend</code>, then put its signing secret in{" "}
                        <code>RESEND_WEBHOOK_SECRET</code>.
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-[11px] text-[#6B6A68]">
                      <div className="p-2 rounded-md border hairline bg-white">
                        <div className="uppercase tracking-[0.18em] text-[9px] text-[#4A5D4E]">Sends from</div>
                        <div className="font-mono truncate" data-testid="email-sender">{del.sender_email}</div>
                      </div>
                      <div className="p-2 rounded-md border hairline bg-white">
                        <div className="uppercase tracking-[0.18em] text-[9px] text-[#4A5D4E]">Suppressed</div>
                        <div data-testid="email-suppressed-count">
                          {del.suppressed_count} address{del.suppressed_count === 1 ? "" : "es"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={runSelfTest} disabled={selfTesting || data.email_disabled}
                        data-testid="email-selftest-btn"
                        title={data.email_disabled
                          ? "EMAIL_DISABLED is on in this environment — nothing would send"
                          : "Send one of every catalogued email to your own inbox"}
                        className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1.5 disabled:opacity-40">
                        <PaperPlaneTilt size={12} /> {selfTesting ? "Sending…" : "Send every email to me"}
                      </button>
                      <span className="text-[11px] text-[#6B6A68]">
                        {data.email_disabled
                          ? "EMAIL_DISABLED is on here — run this in production."
                          : `Proves all ${(data.counts?.user || 0) + (data.counts?.admin || 0)} sendable emails deliver, without staging real refunds.`}
                      </span>
                    </div>
                    {selfTestResult && (
                      <div className="p-3 rounded-md border hairline bg-white text-[11px]" data-testid="email-selftest-result">
                        <div className="text-[#1C1B1A] mb-1.5">
                          <strong>{selfTestResult.sent} sent</strong>
                          {selfTestResult.failed > 0 && <span className="text-[#8A3A22]"> · {selfTestResult.failed} failed</span>}
                          {" "}to {selfTestResult.to}
                        </div>
                        <div className="max-h-40 overflow-auto space-y-0.5">
                          {selfTestResult.results.filter((r) => !r.sent).map((r) => (
                            <div key={r.id} className="text-[#8A3A22]" data-testid={`selftest-fail-${r.id}`}>
                              ✕ {r.title}
                            </div>
                          ))}
                          {selfTestResult.failed === 0 && (
                            <div className="text-[#2F6B3A]">Every catalogued email left the building.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div data-testid="catalog-section-automated">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A5D4E] mb-2 flex items-center gap-1.5">
                    <Globe size={10} weight="duotone" /> Automated · sent to your people ({automated.length})
                  </div>
                  <div className="space-y-3">{automated.map(renderRow)}</div>
                </div>
                {adminOps.length > 0 && (
                  <div data-testid="catalog-section-admin">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A22] mb-2 flex items-center gap-1.5 mt-4">
                      <LockSimple size={10} weight="duotone" /> Admin &amp; ops · sent to you only ({adminOps.length})
                    </div>
                    <div className="text-[11px] text-[#6B6A68] mb-2 leading-relaxed">
                      Operational alerts that never reach a customer. Every one of these was firing
                      invisibly before this sweep — none of them appeared in this list.
                    </div>
                    <div className="space-y-3">{adminOps.map(renderRow)}</div>
                  </div>
                )}
                {manual.length > 0 && (
                  <div data-testid="catalog-section-manual">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#4B3A7A] mb-2 flex items-center gap-1.5 mt-4">
                      <Eye size={10} weight="duotone" /> User-triggered · prefilled mailto bodies ({manual.length})
                    </div>
                    <div className="text-[11px] text-[#6B6A68] mb-2 leading-relaxed">
                      These never go through Resend. The platform composes the subject + body, then opens the user's own email client (Apple Mail, Gmail, Outlook…) so they send from their own inbox. The copy here IS the prefilled draft they see.
                    </div>
                    <div className="space-y-3">{manual.map(renderRow)}</div>
                  </div>
                )}
              </>
            );
          })()}
          <div className="text-[10px] text-[#6B6A68] italic mt-2">
            "Fixed" emails can't be disabled — they're required for auth, for money, or by law
            (password reset, refund receipts, dispute notices, the price-change notice).
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="email-preview-modal">
          <div className="bg-white rounded-xl w-full max-w-xl max-h-[80vh] overflow-auto border hairline p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-serif-display text-xl text-[#1C1B1A]">{preview.title}</h3>
                <div className="text-xs text-[#6B6A68] mt-0.5 font-mono">Subject: {preview.subject_template}</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[#6B6A68] hover:text-[#1C1B1A] text-xl leading-none">×</button>
            </div>
            <div className="bg-[#FDFCF8] border hairline rounded-md p-4 text-sm text-[#1C1B1A] whitespace-pre-wrap font-serif">
              {preview.preview_text}
            </div>
            <div className="mt-3 text-[10px] text-[#6B6A68] space-y-1">
              <div><span className="uppercase tracking-[0.18em] text-[#4A5D4E]">Trigger:</span> {preview.when}</div>
              <div><span className="uppercase tracking-[0.18em] text-[#4A5D4E]">Recipient:</span> {preview.to}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
